import { Icon } from "@zapier/zinnia-icons";

import {
  BetaDataTableCell as Cell,
  Button,
  Spacer,
  Spinner,
  Tooltip,
  TooltipFocusableTrigger,
  Text,
} from "@zapier/design-system";

import { Connection, useConnectionActionPermissions } from "@zapier/client";
import {
  ConnectionKebabInteractionOption,
  useEmitConnectionKebabInteractionEvent,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";

import {
  getStaleReferenceDate,
  isStaleSuspect as computeIsStaleSuspect,
} from "../staleSuspect";
import styles from "./StatusCell.module.css";

interface StatusCellProps {
  appName?: string;
  isError?: boolean;
  isLoading?: boolean;
  isReconnectLoading?: boolean;
  isNonCompliant?: boolean;
  isRestricted?: boolean;
  isStale: boolean;
  isDisplayOnly?: boolean;
  isAppUnavailable?: boolean;
  connection: Connection;
  onReconnect: () => void;
  onReplace: () => void;
  onTest: () => void;
}

const StatusLabel = ({
  appName,
  isError,
  isLoading,
  isNonCompliant,
  isRestricted,
  isStale,
  showSuspectCue,
  staleReferenceDate,
}: Pick<
  StatusCellProps,
  | "appName"
  | "isError"
  | "isLoading"
  | "isNonCompliant"
  | "isRestricted"
  | "isStale"
> & {
  showSuspectCue: boolean;
  staleReferenceDate: Date | undefined;
}) => {
  const t = useTranslations("connections-table-reconnect-cell");

  // Priority: loading/testing > restricted > expired > non-compliant > connected (+ optional stale-suspect cue)
  if (isRestricted && !isLoading) {
    const restrictedTooltip = t("tooltips.restricted", {
      appName: appName || "",
    });
    return (
      <div className={styles.statusLabel}>
        <Icon name="formRadioDot" size={20} color="TextDisabled" />
        <Text type="Body1">{t("labels.disabled")}</Text>
        <Tooltip allowMultiline content={restrictedTooltip} placement="bottom">
          <TooltipFocusableTrigger>
            <div aria-label={restrictedTooltip} role="img">
              <Icon name="alertInfo" size={16} color="StrokeStronger" />
            </div>
          </TooltipFocusableTrigger>
        </Tooltip>
      </div>
    );
  }

  if ((isStale || isError) && !isLoading) {
    return (
      <div className={styles.statusLabel}>
        <Icon name="formRadioDot" size={20} color="Red4" />
        <Text type="Body1">{t("labels.expired")}</Text>
      </div>
    );
  }

  if (isNonCompliant && !isLoading) {
    return (
      <div className={styles.statusLabel}>
        <Icon name="formRadioDot" size={20} color="Yellow3" />
        <Text type="Body1">{t("labels.non-compliant")}</Text>
        <Tooltip
          allowMultiline
          content={t("tooltips.non-compliant")}
          placement="bottom"
        >
          <TooltipFocusableTrigger>
            <div aria-label={t("tooltips.non-compliant")} role="img">
              <Icon name="alertInfo" size={16} color="StrokeStronger" />
            </div>
          </TooltipFocusableTrigger>
        </Tooltip>
      </div>
    );
  }

  if (isLoading) {
    return (
      <Tooltip content={t("tooltips.testing-connection")} placement="bottom">
        <TooltipFocusableTrigger>
          <Spinner size="small" />
        </TooltipFocusableTrigger>
      </Tooltip>
    );
  }

  const suspectTooltip =
    showSuspectCue && staleReferenceDate
      ? t("tooltips.stale-suspect", { date: staleReferenceDate })
      : undefined;

  return (
    <div className={styles.statusLabel}>
      <Icon name="formRadioDot" size={20} color="PrimeGreen" />
      <Text type="Body1">{t("labels.connected")}</Text>
      {suspectTooltip ? (
        <Tooltip allowMultiline content={suspectTooltip} placement="bottom">
          <TooltipFocusableTrigger>
            <div aria-label={suspectTooltip} role="img">
              <Icon name="alertInfo" size={16} color="StrokeStronger" />
            </div>
          </TooltipFocusableTrigger>
        </Tooltip>
      ) : undefined}
    </div>
  );
};

export const StatusCell = ({
  appName,
  isDisplayOnly,
  isError,
  isLoading,
  isReconnectLoading,
  isNonCompliant,
  isRestricted,
  isStale,
  isAppUnavailable,
  connection,
  onReconnect,
  onReplace,
  onTest,
}: StatusCellProps) => {
  const t = useTranslations("connections-table-reconnect-cell");
  const { canReconnectConnection, canReplaceConnection, canTestConnection } =
    useConnectionActionPermissions({ connection });

  const { emit } = useEmitConnectionKebabInteractionEvent();

  const isStaleSuspect = computeIsStaleSuspect(connection);
  const staleReferenceDate = getStaleReferenceDate(connection);

  const showReconnect =
    isStale && canReconnectConnection && !isDisplayOnly && !isRestricted;
  const showReplace =
    isNonCompliant &&
    !isStale &&
    canReplaceConnection &&
    !isDisplayOnly &&
    !isRestricted;
  const showTest =
    isStaleSuspect &&
    !isNonCompliant &&
    !isLoading &&
    !isDisplayOnly &&
    canTestConnection &&
    !isAppUnavailable &&
    !isRestricted;

  return (
    <Cell className={styles.cell}>
      <div className={styles.root}>
        <StatusLabel
          appName={appName}
          isError={isError}
          isLoading={isLoading}
          isNonCompliant={isNonCompliant}
          isRestricted={isRestricted}
          isStale={isStale}
          showSuspectCue={showTest}
          staleReferenceDate={staleReferenceDate}
        />
        {showReconnect ? (
          isReconnectLoading ? (
            <Button variant="tertiary" size="compact" isDisabled>
              {t("buttons.reconnect")}
            </Button>
          ) : isAppUnavailable ? (
            <Tooltip content={t("tooltips.app-unavailable")} placement="top">
              <TooltipFocusableTrigger>
                <Button variant="tertiary" size="compact" isDisabled>
                  {t("buttons.reconnect")}
                </Button>
              </TooltipFocusableTrigger>
            </Tooltip>
          ) : (
            <>
              <Tooltip
                content={t("tooltips.reconnect-credential")}
                allowMultiline
              >
                <TooltipFocusableTrigger>
                  <div
                    aria-label={t("tooltips.reconnect-credential")}
                    role="img"
                  >
                    <Icon name="alertInfo" size={16} color="StrokeStronger" />
                  </div>
                </TooltipFocusableTrigger>
              </Tooltip>
              <Button
                variant="tertiary"
                size="compact"
                onPress={() => {
                  emit(ConnectionKebabInteractionOption.Reconnect);
                  onReconnect();
                }}
              >
                {t("buttons.reconnect")}
              </Button>
            </>
          )
        ) : showReplace ? (
          <Button
            variant="tertiary"
            size="compact"
            onPress={() => {
              emit(ConnectionKebabInteractionOption.Replace);
              onReplace();
            }}
          >
            {t("buttons.replace")}
          </Button>
        ) : showTest ? (
          <Button
            variant="tertiary"
            size="compact"
            onPress={() => {
              emit(ConnectionKebabInteractionOption.TestConnection);
              onTest();
            }}
          >
            {t("buttons.test-connection")}
          </Button>
        ) : (
          <Spacer width={88} />
        )}
      </div>
    </Cell>
  );
};
